import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry, of, delay } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ExamInformation {
  id?: string;
  examTitle?: string;
  valuerRank?: string;
  registrationDeadline?: string;
  examDate?: string;
  examTime?: string;
  examLocation?: string;
  examFee?: string;
  remarks?: string;
  posterFile?: string | null;
  certificateFile?: string | null;
  createdAt?: string;
  updatedAt?: string;
  examStatusId?: number;
  examStatus?: {
    id: number;
    name: string;
    createdAt?: string;
    updatedAt?: string;
  };
  cpdHours?: any[];
}

export interface ExamFilters {
  searchTerm: string;
  category: string;
  level: string;
  status: string;
  instructor: string;
}

export interface ExamPaginatedResponse {
  exams: ExamInformation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExamService {
  private readonly API_BASE = environment.apiUrl;
  private readonly API_TIMEOUT = 30000; // 30 seconds
  private readonly MAX_RETRIES = 2;

  constructor(private http: HttpClient) {
    console.log('🌐 ExamService API URL:', this.API_BASE);
  }

  /**
   * Get all exams
   */
  getAllExams(): Observable<ExamInformation[]> {
    console.log('🔄 ExamService: กำลังเรียกข้อมูลการสอบจาก backend API...');
    console.log('🌐 API URL:', `${this.API_BASE}/exams`);
    
    return this.http.get<ExamInformation[]>(`${this.API_BASE}/exams`)
      .pipe(
        timeout(this.API_TIMEOUT),
        retry(this.MAX_RETRIES),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get exam by ID
   */
  getExamById(id: string): Observable<ExamInformation> {
    console.log('🔄 ExamService: กำลังเรียกข้อมูลการสอบ ID จาก backend API:', id);
    console.log('🌐 API URL:', `${this.API_BASE}/exams/${id}`);
    
    return this.http.get<ExamInformation>(`${this.API_BASE}/exams/${id}`)
      .pipe(
        timeout(this.API_TIMEOUT),
        retry(this.MAX_RETRIES),
        catchError((error: HttpErrorResponse) => {
          console.error('❌ ExamService: ไม่สามารถเชื่อมต่อ exam details API:', error.message);
          console.error('❌ Error status:', error.status, 'Error details:', error.error);
          
          let errorMessage = 'ไม่สามารถโหลดข้อมูลการสอบได้';
          
          switch (error.status) {
            case 0:
              errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
              break;
            case 404:
              errorMessage = 'ไม่พบการสอบที่ต้องการ';
              break;
            case 401:
              errorMessage = 'กรุณาเข้าสู่ระบบใหม่';
              break;
            case 403:
              errorMessage = 'คุณไม่มีสิทธิ์เข้าถึงข้อมูลนี้';
              break;
            case 500:
              errorMessage = 'เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์';
              break;
          }
          
          return throwError(() => errorMessage);
        })
      );
  }

  /**
   * Create new exam
   */
  createExam(exam: Partial<ExamInformation>): Observable<ExamInformation> {
    return this.http.post<ExamInformation>(`${this.API_BASE}/exams`, exam)
      .pipe(
        timeout(this.API_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update existing exam
   */
  updateExam(id: string, exam: Partial<ExamInformation>): Observable<ExamInformation> {
    return this.http.put<ExamInformation>(`${this.API_BASE}/exams/${id}`, exam)
      .pipe(
        timeout(this.API_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Delete exam
   */
  deleteExam(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE}/exams/${id}`)
      .pipe(
        timeout(this.API_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Get mock data when API is not available
   */
  private getMockExams(): Observable<ExamInformation[]> {
    const mockExams: ExamInformation[] = [
      {
        id: '1',
        examTitle: 'แบบทดสอบบัญชีเบื้องต้น',
        valuerRank: 'ระดับ 1',
        registrationDeadline: '2024-02-15',
        examDate: '2024-02-20',
        examTime: '09:00-11:00',
        examLocation: 'ห้องประชุมใหญ่ ชั้น 5',
        examFee: '500.00',
        remarks: 'กรุณานำบัตรประชาชนมาในวันสอบ',
        posterFile: null,
        certificateFile: null,
        createdAt: '2024-01-01T00:00:00.000Z',
        updatedAt: '2024-01-01T00:00:00.000Z',
        examStatusId: 1,
        examStatus: {
          id: 1,
          name: 'เตรียมเปิดสอบ'
        },
        cpdHours: []
      }
    ];

    return of(mockExams).pipe(delay(500));
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `ข้อผิดพลาดจากไคลเอนต์: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 0:
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
          return this.getMockExams() as any;
        case 400:
          errorMessage = 'ข้อมูลที่ส่งมาไม่ถูกต้อง';
          break;
        case 401:
          errorMessage = 'กรุณาเข้าสู่ระบบใหม่';
          break;
        case 403:
          errorMessage = 'คุณไม่มีสิทธิ์เข้าถึงข้อมูลนี้';
          break;
        case 404:
          errorMessage = 'ไม่พบข้อมูลที่ต้องการ';
          break;
        case 500:
          errorMessage = 'เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์';
          break;
        default:
          errorMessage = `เกิดข้อผิดพลาด: ${error.status} ${error.statusText}`;
      }
    }

    console.error('Exam Service Error:', error);
    return throwError(() => errorMessage);
  }
}
